import { useEffect, useRef, useState } from "react";
import { SubmissionGate, type SubmissionIntent } from "./submissionIntent";

// Verification is an ephemeral intent, never a token cached while editing.
export function useVerifiedSubmission(contents: string, submit: (token: string) => Promise<void>) {
  const [intent, setIntent] = useState<SubmissionIntent | null>(null);
  const [busy, setBusy] = useState(false);
  const gate = useRef(new SubmissionGate());
  const current = useRef(contents);
  const callback = useRef(submit);
  current.current = contents;
  callback.current = submit;
  // Invalidate synchronously, even if a challenge callback beats the next effect.
  if (gate.current.pending && gate.current.pending.contents !== contents) gate.current.cancel();
  function cancel() { gate.current.cancel(); setIntent(null); }
  useEffect(() => () => { gate.current.cancel(); }, []);
  function request() {
    const next = gate.current.request(contents);
    if (next) setIntent(next);
  }
  async function verified(id: string, token: string) {
    if (!gate.current.consume(id, current.current, token)) return;
    setIntent(null); setBusy(true);
    try { await callback.current(token); }
    finally { gate.current.finish(); setBusy(false); }
  }
  return { intent: intent?.id === gate.current.pending?.id ? intent : null, busy, request, cancel, verified };
}
